import { useEffect, useRef, useState } from 'react';
import { WorkspaceDialog } from './WorkspaceDialog';
import { TooltipButton } from './TooltipButton';
import type { DocumentResult } from '../shared/desktop-api';
import { sourcePageIdentity } from './field-source-geometry';
import {
  sourcePreviewCache,
  watchSourcePreview,
  type SourcePreview,
} from './field-source-preview-cache';

/** Orientation for the operator, not a claim of classifier grounding. */
export function DocumentTypePreview({
  document,
  token,
}: {
  document: DocumentResult;
  token: string;
}) {
  const page = document.pages[0];
  const identity = page ? sourcePageIdentity(document.id, token, page) : '';
  const [loaded, setLoaded] = useState<{
    identity: string;
    preview?: SourcePreview;
    failed?: boolean;
  }>();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [pan, setPan] = useState({ identity: '', y: 0 });
  const drag = useRef<{
    pointer: number;
    identity: string;
    y: number;
    origin: number;
    scale: number;
  } | null>(null);
  const moved = useRef(false);
  useEffect(() => {
    if (!page) return;
    return watchSourcePreview(
      sourcePreviewCache,
      identity,
      page.previewPath,
      (preview) => setLoaded({ identity, preview }),
      () => setLoaded({ identity, failed: true }),
    );
  }, [identity]);
  const current = loaded?.identity === identity ? loaded : undefined;
  const preview = current?.preview;
  const height = preview ? Math.round(preview.height * 0.3) : 0;
  const y = pan.identity === identity ? pan.y : 0;
  return (
    <>
      <TooltipButton
        type="button"
        className="document-type-preview"
        onClick={() => {
          if (moved.current) {
            moved.current = false;
            return;
          }
          setExpanded(identity);
        }}
        disabled={!preview}
        aria-label="Open full page"
        tooltip="Click to open the full page"
      >
        {preview ? (
          <svg
            role="img"
            aria-label="Document type page preview"
            viewBox={`0 ${y} ${preview.width} ${height}`}
            preserveAspectRatio="xMidYMin meet"
            style={{ cursor: 'grab', touchAction: 'none' }}
            onPointerDown={(event) => {
              if (event.button !== 0 || !event.isPrimary) return;
              const scale = event.currentTarget.getScreenCTM()?.a;
              if (!scale || scale <= 0) return;
              moved.current = false;
              drag.current = {
                pointer: event.pointerId,
                identity,
                y: event.clientY,
                origin: y,
                scale,
              };
              event.currentTarget.setPointerCapture(event.pointerId);
              event.preventDefault();
            }}
            onPointerMove={(event) => {
              const active = drag.current;
              if (!active || active.identity !== identity || active.pointer !== event.pointerId)
                return;
              const delta = event.clientY - active.y;
              if (!moved.current && Math.abs(delta) < 4) return;
              moved.current = true;
              setPan({
                identity,
                y: Math.max(
                  0,
                  Math.min(preview.height - height, active.origin - delta / active.scale),
                ),
              });
            }}
            onPointerUp={(event) => {
              if (drag.current?.pointer !== event.pointerId) return;
              drag.current = null;
              event.currentTarget.releasePointerCapture(event.pointerId);
            }}
            onPointerCancel={() => {
              drag.current = null;
              moved.current = true;
            }}
            onLostPointerCapture={() => {
              drag.current = null;
            }}
          >
            <image href={preview.source} width={preview.width} height={preview.height} />
          </svg>
        ) : (
          <span>
            {current?.failed ? 'Open page to check document type' : 'Loading page header…'}
          </span>
        )}
        <small>Open full page</small>
      </TooltipButton>
      {expanded === identity && preview && (
        <WorkspaceDialog
          telemetry="type-preview"
          title="Document type · full page"
          onClose={() => setExpanded(null)}
        >
          <img
            src={preview.source}
            alt="Full first page for checking document type"
            style={{ display: 'block', width: '100%', height: 'auto' }}
          />
        </WorkspaceDialog>
      )}
    </>
  );
}
